import { Controller } from "stimulus"

// Horizontally scrolling logo band used by the `giving_days_carousel` landing
// page section. Splide is loaded lazily so its ~30KB only lands on pages that
// actually render the section.
//
// Values come from the section's admin form:
//   perPage    - logos visible at once on desktop
//   autoScroll - continuous marquee drift (arrows still work)
//   speed      - pixels per frame for the marquee
//
// Stimulus 2 has no value defaults, so every value is read through a guard.
let Splide, AutoScroll
let splideLoaded = false

async function loadSplide() {
  if (splideLoaded) return

  // Core CSS only: the themed stylesheets fight the giving day palette, and
  // everything visual lives in giving_days_landing.css.scss instead.
  const [core, autoScroll] = await Promise.all([
    import("@splidejs/splide"),
    import("@splidejs/splide-extension-auto-scroll"),
    import("@splidejs/splide/dist/css/splide-core.min.css")
  ])

  Splide = core.default || core.Splide
  AutoScroll = autoScroll.AutoScroll
  splideLoaded = true
}

export default class extends Controller {
  static values = {
    perPage: Number,
    autoScroll: Boolean,
    speed: Number
  }

  async connect() {
    await loadSplide()

    // connect() is async, so the element can be gone by the time Splide lands
    if (!this.element.isConnected) return

    const perPage = this.hasPerPageValue && this.perPageValue > 0 ? this.perPageValue : 6
    const autoScroll = this.hasAutoScrollValue ? this.autoScrollValue : true
    const speed = this.hasSpeedValue && this.speedValue !== 0 ? this.speedValue : 1

    const options = {
      // "loop" is what lets the marquee wrap seamlessly instead of stopping dead
      type: autoScroll ? "loop" : "slide",
      perPage: perPage,
      perMove: 1,
      gap: "2.5rem",
      arrows: true,
      pagination: false,
      drag: "free",
      omitEnd: true,
      breakpoints: {
        1200: { perPage: Math.max(1, perPage - 1) },
        992: { perPage: Math.max(1, perPage - 2) },
        768: { perPage: 2 },
        576: { perPage: 1 }
      }
    }

    if (autoScroll) {
      options.autoScroll = {
        speed: speed,
        pauseOnHover: true,
        pauseOnFocus: true
      }
    }

    this.splide = new Splide(this.element, options)
    this.splide.mount(autoScroll ? { AutoScroll } : {})
  }

  disconnect() {
    if (this.splide) {
      this.splide.destroy()
      this.splide = null
    }
  }
}
